/**
 * MarketplaceOrdersPage.tsx — every Marketplace app's orders in one list.
 *
 * Reached only from the Marketplace dashboard (no sidebar entry, on purpose):
 * a summary tile's `?show=all|month|cancelled|awaiting`, or the Open button of
 * an app with no page of its own (`?vendor=<key>`, its orders as the card
 * counts them), sets the filters to those records, then leaves the URL, so a
 * later filter change or refresh keeps what the user picked. The dashboard
 * counts only ready apps within the sidebar's facility lock, so a preset
 * clears every other filter.
 *
 * VIEW-ONLY ON PURPOSE. The order actions (Finish payment, Cancel, Retry…) run
 * one vendor's flow at a time (useAxiomOrderFlow), and the Peptides and Partell
 * pages each own theirs. A row here opens the patient's chart where the order
 * is — by patient ID, never by name — and those buttons are there.
 *
 * The filters are the vendor order pages' own (INTEGRATION_ORDER_FILTER_CONFIGS)
 * plus Vendor, resolved server-side by GET /integration-orders through the
 * `integration_order` pipeline. The list is narrowed to the ready apps' vendor
 * keys, as the dashboard counts them; Vitaboom keeps its orders on its side.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { Spinner } from '@/components/ui/Spinner';
import { useIntegrationOrderFilterOptions } from '@/hooks/filters';
import { resolvePreset } from '@/lib/dateRangePresets';
import { buildApiParams, FILTER_KEYS } from '@/lib/filterRegistry';
import { parseDateValue } from '@/lib/utils';
import { useFacilityOverrideValues, useLocalDashboardFilters } from '@/pages/dashboard/dashboardFilters';
import { getAccountIntegrationOrders, type IntegrationOrder } from '@/services/integrations.service';
import { fulfillmentVariant } from '@/pages/patients/labs/IntegrationOrdersSubTab';
import { useAuthStore } from '@/store/authStore';
import type { FilterConfig, FilterValue } from '@/types/filters.types';
import { INTEGRATION_ORDER_FILTER_CONFIGS, NO_FACILITY } from './integrationOrdersShared';
import {
  MARKETPLACE_ORDERS_VIEWS,
  ORDERS_LEVEL,
  appsByVendorKey,
  isOrderCancelled,
  orderChartHref,
  useMarketplaceApps,
  type MarketplaceOrdersView,
} from './marketplaceApps';
import { AppTile } from './MarketplacePage';

/** The vendor pages' filters, with Vendor after Search: this page lists every vendor. */
const FILTER_CONFIGS: FilterConfig[] = [
  INTEGRATION_ORDER_FILTER_CONFIGS[0],
  { key: FILTER_KEYS.INTEGRATION, label: 'Vendor', type: 'select', allLabel: 'Any vendor', multi: true },
  ...INTEGRATION_ORDER_FILTER_CONFIGS.slice(1),
];

/** As on the Leads page. */
const PAGE_SIZE = 100;

/**
 * The filters each dashboard tile's records need, every other filter cleared.
 * Total orders and Added this month count cancelled orders too, so they show
 * them; Cancelled asks for the 'cancelled' status, which the API also reads as
 * a request for cancelled orders (ListFilterService). A `vendor` narrows any
 * of them to that vendor; on its own it is the 'all' view of the vendor, the
 * records an app card's order count covers.
 */
export function presetFilters(
  view: MarketplaceOrdersView | null,
  vendor: string | null = null,
  now: Date = new Date(),
): Record<string, FilterValue> {
  const filters = viewFilters(view ?? 'all', now);
  return vendor ? { ...filters, [FILTER_KEYS.INTEGRATION]: [vendor] } : filters;
}

function viewFilters(view: MarketplaceOrdersView, now: Date): Record<string, FilterValue> {
  switch (view) {
    case 'all':
      return { [FILTER_KEYS.INCLUDE_CANCELLED]: ['1'] };
    case 'month':
      return {
        [FILTER_KEYS.INCLUDE_CANCELLED]: ['1'],
        [FILTER_KEYS.DATERANGE]: { ...resolvePreset('thismonth', now), preset: 'thismonth' },
      };
    case 'cancelled':
      return { [FILTER_KEYS.ORDER_STATUS]: ['cancelled'] };
    case 'awaiting':
      return { [FILTER_KEYS.ORDER_STATUS]: ['awaiting_payment'] };
  }
}

/** "Aug 8, 2026", as the dashboard writes it. */
function formatOrderDate(value: string | null | undefined): string {
  const date = parseDateValue(value);
  return date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

export default function MarketplaceOrdersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const canSeeOrders = (user?.userLevel ?? 0) >= ORDERS_LEVEL;
  const { ready: apps, checking } = useMarketplaceApps();
  const appByKey = useMemo(() => appsByVendorKey(apps), [apps]);
  const [page, setPage] = useState(1);

  const filterState = useLocalDashboardFilters(FILTER_CONFIGS, 'marketplace-orders');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);
  // Under a global facility override, orders with no facility stay reachable (as on Partell).
  const pageValues = useMemo(() => {
    if (!isFacilityLocked) return effectiveValues;
    const ids = Array.isArray(effectiveValues.facility) ? effectiveValues.facility : [];
    return { ...effectiveValues, [FILTER_KEYS.FACILITY]: [...ids, NO_FACILITY] };
  }, [effectiveValues, isFacilityLocked]);

  // A tile's or an app's preset: applied once, then taken out of the URL.
  const show = searchParams.get('show');
  const vendor = searchParams.get('vendor');
  const pending = Boolean(show || vendor);
  const { setFilter, clearFilter } = filterState;
  useEffect(() => {
    if (!show && !vendor) return;
    const view = MARKETPLACE_ORDERS_VIEWS.find((v) => v === show) ?? null;
    if (view || vendor) {
      const preset = presetFilters(view, vendor);
      for (const config of FILTER_CONFIGS) {
        if (config.key === FILTER_KEYS.FACILITY && isFacilityLocked) continue;
        if (config.key in preset) setFilter(config.key, preset[config.key]);
        else clearFilter(config.key);
      }
      setPage(1);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('show');
    next.delete('vendor');
    setSearchParams(next, { replace: true });
  }, [show, vendor, searchParams, setSearchParams, setFilter, clearFilter, isFacilityLocked]);

  const apiParams = useMemo(
    () => buildApiParams(pageValues, filterState.negations),
    [pageValues, filterState.negations],
  );
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['account-integration-orders', 'marketplace', apiParams],
    queryFn: () => getAccountIntegrationOrders(undefined, apiParams),
    staleTime: 30_000,
    // No placeholderData: a tile's preset or a filter change shows the loader,
    // never the previous filters' rows (they read as the new list).
    enabled: canSeeOrders && !pending,
  });
  const rows = useMemo(
    () => orders.filter((o) => o.vendorKey && appByKey.has(o.vendorKey)),
    [orders, appByKey],
  );
  useEffect(() => { setPage(1); }, [apiParams]);

  const options = useIntegrationOrderFilterOptions({ enabled: canSeeOrders });
  const sidebarOptions = useMemo(() => ({
    ...options,
    // Only the ready apps' vendors, as the list is.
    [FILTER_KEYS.INTEGRATION]: (options[FILTER_KEYS.INTEGRATION] ?? []).filter((o) => appByKey.has(o.value)),
  }), [options, appByKey]);
  const statusLabel = (status: string) =>
    options[FILTER_KEYS.ORDER_STATUS]?.find((o) => o.value === status)?.label ?? status.replace(/_/g, ' ');

  const hasActiveFilters = Object.entries(pageValues).some(([key, value]) => {
    if (key === FILTER_KEYS.FACILITY && isFacilityLocked) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === 'object') return Boolean((value as { from?: string }).from);
    return Boolean(value);
  });

  const columns: DataTableColumn<IntegrationOrder>[] = [
    {
      key: 'patientName',
      header: 'Patient',
      role: 'title',
      render: (o) => <span className="font-medium text-slate-900">{o.patientName || 'Unknown patient'}</span>,
    },
    {
      key: 'vendorKey',
      header: 'App',
      render: (o) => {
        const app = o.vendorKey ? appByKey.get(o.vendorKey) : undefined;
        return (
          <span className="inline-flex items-center gap-2">
            {app && <AppTile app={app} size="sm" />}
            <span>{app?.name ?? o.vendorLabel ?? '—'}</span>
          </span>
        );
      },
    },
    { key: 'createdAt', header: 'Ordered', render: (o) => formatOrderDate(o.createdAt) },
    {
      key: 'fulfillmentStatus',
      header: 'Status',
      // The chart's colours: green delivered, blue on its way, red stopped, amber in progress.
      render: (o) => (isOrderCancelled(o)
        ? <Badge variant="error" dot>Cancelled</Badge>
        : o.fulfillmentStatus
          ? <Badge variant={fulfillmentVariant(o.fulfillmentStatus)} dot>{statusLabel(o.fulfillmentStatus)}</Badge>
          : <Badge variant="neutral" dot>Placed</Badge>),
    },
    { key: 'orderedBy', header: 'Ordered by', render: (o) => o.orderedBy || '—' },
  ];

  const openOrder = (o: IntegrationOrder) => {
    const href = orderChartHref(o.vendorKey ? appByKey.get(o.vendorKey) : undefined, o);
    if (href) navigate(href);
  };

  return (
    <ListPageLayout
      filterSidebar={canSeeOrders ? (
        <FilterSidebar
          filters={FILTER_CONFIGS}
          values={effectiveValues}
          options={sidebarOptions}
          negations={filterState.negations}
          onChange={filterState.setFilter}
          onNegateChange={filterState.setNegation}
          onClear={filterState.clearFilter}
          lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          searchCommitMode="blur-or-enter"
          showAIAssistant={false}
          storageKey="marketplace-orders"
        />
      ) : undefined}
    >
      {/* The Leads page's shape: the header stays put and the table scrolls, so
          its pagination footer stays on screen (ListPageLayout clips overflow). */}
      <div className="flex h-full min-h-0 flex-col overflow-x-hidden max-md:h-auto">
        <div className="shrink-0 p-6 pb-4">
          <PageHeader
            variant="compact"
            title="Marketplace Orders"
            subtitle="Every Marketplace app's orders. Open one to see it in the patient's chart."
          />
        </div>
        <div className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6 pb-6 max-md:overflow-visible">
          {!canSeeOrders ? (
            <p className="py-8 text-center text-sm text-slate-500">
              Marketplace orders are available to staff at user level 2 and above.
            </p>
          ) : checking ? (
            <div className="flex justify-center py-16"><Spinner size="lg" /></div>
          ) : apps.every((app) => !app.integrationKey) ? (
            <p className="py-8 text-center text-sm text-slate-500">
              No Marketplace app that takes orders is set up for your practice yet.
            </p>
          ) : (
            <DataTable<IntegrationOrder>
              data={rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)}
              columns={columns}
              keyField="integrationOrderId"
              loading={isLoading || pending}
              page={page}
              pageSize={PAGE_SIZE}
              total={rows.length}
              onPageChange={setPage}
              onRowClick={openOrder}
              emptyTitle={hasActiveFilters ? 'No orders match your filters' : 'No orders yet'}
              emptyDescription={hasActiveFilters
                ? 'Try clearing a filter.'
                : 'Orders placed through your Marketplace apps will appear here.'}
            />
          )}
        </div>
      </div>
    </ListPageLayout>
  );
}
